import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const usePaginationHandler = ({ products }) => {
  const showPagination = 5;
  const showLimit = 20;

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState([]);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(showLimit);

  // 페이지네이션 생성
  const createPagination = () => {
    if (!products.items) return;

    const totalPages = Math.ceil(products.items.length / limit);
    const pageNumbers = new Array(totalPages).fill().map((_, idx) => idx + 1);

    setPagination(pageNumbers);
    setPages(totalPages);

    // 페이지 수가 변경되면 현재 페이지 범위 확인
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  };

  // 현재 페이지에 해당하는 제품 목록 계산
  const getPagedProducts = () => {
    if (!products.items || !products.items.length) return [];

    const startIdx = (currentPage - 1) * limit;
    const endIdx = startIdx + limit;

    return products.items.slice(startIdx, endIdx);
  };

  // 페이지네이션 컨트롤 함수
  const next = () => {
    if (currentPage < pages) {
      window.scrollTo(0, 520);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prev = () => {
    if (currentPage > 1) {
      window.scrollTo(0, 520);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleActive = (page) => {
    window.scrollTo(0, 520);
    setCurrentPage(page);
  };

  const nextEnd = () => {
    window.scrollTo(0, 520);
    setCurrentPage(pages);
  };

  const prevStart = () => {
    window.scrollTo(0, 520);
    setCurrentPage(1);
  };

  // 페이지네이션 그룹 계산
  const getPaginationGroup = () => {
    const groupStart =
      Math.floor((currentPage - 1) / showPagination) * showPagination;
    return pagination.slice(groupStart, groupStart + showPagination);
  };

  useEffect(() => {
    createPagination();

    setCurrentPage(1);
  }, [products.items]);

  return {
    next,
    prev,
    handleActive,
    nextEnd,
    prevStart,
    getPagedProducts,
    getPaginationGroup,
    createPagination,
    currentPage,
    pagination,
    pages,
    limit,
  };
};

export const usePaginationHandlerModified = ({
  products,
  totalPages = 0,
  initialPage = 1,
}) => {
  const showPagination = 5;
  const showLimit = 20;
  const prevProductsRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pagination, setPagination] = useState([]);
  const [pages, setPages] = useState(totalPages || 1);
  const [limit, setLimit] = useState(showLimit);

  const resetPage = useCallback((newPage = 1) => {
    setCurrentPage(newPage);
  }, []);

  // 페이지네이션 생성
  const createPagination = useCallback(() => {
    if (!products) return;

    // 총 페이지 수 계산
    let totalPages;
    if (products.total_page) {
      // 서버에서 제공하는 총 페이지 수 사용
      totalPages = products.total_page;
    } else if (products.total_count) {
      // 총 항목 수로 계산
      totalPages = Math.ceil(products.total_count / limit);
    } else if (products.items && products.items.length) {
      // 아이템 배열 길이로 계산
      totalPages = Math.ceil(products.items.length / limit);
    } else if (products.data && products.data.length) {
      // 이미 페이지네이션된 데이터인 경우
      totalPages = Math.ceil(products.data.length / limit);
    } else {
      // 기본값
      totalPages = 1;
    }

    // 페이지 수가 변경된 경우에만 업데이트
    if (totalPages !== pages) {
      setPages(totalPages);

      // totalPages가 1일 경우 페이지네이션 업데이트를 하지 않음
      if (totalPages > 1) {
        const newPagination = Array.from(
          { length: totalPages },
          (_, i) => i + 1
        );
        setPagination(newPagination);
      } else {
        setPagination([1]); // totalPages가 1일 경우 페이지네이션을 [1]로 설정
      }
    }

    // 이전 products 값 저장
    prevProductsRef.current = products;
  }, [products, limit, pages]);

  // 현재 페이지에 해당하는 제품 목록 계산
  const getPagedProducts = useCallback(() => {
    if (products.data) {
      return products.data;
    }

    // 클라이언트 사이드 페이지네이션 로직
    if (!products.items || !products.items.length) return [];

    const startIdx = (currentPage - 1) * limit;
    const endIdx = startIdx + limit;

    return products.items.slice(startIdx, endIdx);
  }, [currentPage, limit, products]);

  // 페이지네이션 컨트롤 함수
  const next = () => {
    if (currentPage < pages) {
      window.scrollTo(0, 520);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prev = () => {
    if (currentPage > 1) {
      window.scrollTo(0, 520);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleActive = (page) => {
    window.scrollTo(0, 520);
    setCurrentPage(page);
  };

  const nextEnd = () => {
    window.scrollTo(0, 520);
    setCurrentPage(pages);
  };

  const prevStart = () => {
    window.scrollTo(0, 520);
    setCurrentPage(1);
  };

  // 페이지네이션 그룹 계산
  const getPaginationGroup = () => {
    // 현재 페이지가 중앙에 오도록 계산
    let start = Math.max(
      0,
      Math.min(
        pagination.length - showPagination,
        currentPage - Math.ceil(showPagination / 2)
      )
    );

    const result = pagination.slice(start, start + showPagination);

    if (result.length === 1) return [];
    return result;
  };

  // products가 변경될 때마다 페이지네이션 재생성
  useEffect(() => {
    createPagination();
  }, [products, createPagination]);

  // initialPage가 변경되면 currentPage 동기화
  useEffect(() => {
    if (initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage]);

  return {
    next,
    prev,
    handleActive,
    nextEnd,
    prevStart,
    getPagedProducts,
    getPaginationGroup,
    createPagination,
    currentPage,
    pagination,
    pages,
    limit,
    resetPage,
  };
};
